import React, { useMemo } from "react"
import { Link, graphql, useStaticQuery } from "gatsby"

const DESTINATION_META = {
  mauritius: {
    en: { label: "Mauritius", path: "/en/properties-mauritius" },
    fr: { label: "Ile Maurice", path: "/fr/proprietes-ile-maurice" },
    countryCandidates: ["mauritius", "ile maurice"],
  },
  dubai: {
    en: { label: "Dubai", path: "/en/properties-dubai" },
    fr: { label: "Dubai", path: "/fr/proprietes-dubai" },
    countryCandidates: ["united arab emirates", "emirats arabes unis", "dubai"],
  },
  marbella: {
    en: { label: "Marbella", path: "/en/properties-marbella" },
    fr: { label: "Marbella", path: "/fr/proprietes-marbella" },
    countryCandidates: ["spain", "espagne", "marbella"],
  },
  france: {
    en: { label: "France", path: "/en/properties-france" },
    fr: { label: "France", path: "/fr/proprietes-france" },
    countryCandidates: ["france"],
  },
  greece: {
    en: { label: "Greece", path: "/en/properties-greece" },
    fr: { label: "Grece", path: "/fr/proprietes-grece" },
    countryCandidates: ["greece", "grece", "gr ce"],
  },
  "united-states": {
    en: { label: "United States", path: "/en/properties-united-states" },
    fr: { label: "Etats-Unis", path: "/fr/proprietes-etats-unis" },
    countryCandidates: ["united states", "etats-unis", "etats unis", "usa"],
  },
  maldives: {
    en: { label: "Maldives", path: "/en/properties-maldives" },
    fr: { label: "Maldives", path: "/fr/proprietes-maldives" },
    countryCandidates: ["maldives"],
  },
}

const PRIORITY_BY_CURRENT = {
  dubai: ["mauritius", "marbella", "france"],
  mauritius: ["dubai", "marbella", "france"],
  marbella: ["france", "dubai", "greece"],
  france: ["marbella", "dubai", "mauritius"],
  greece: ["marbella", "france", "dubai"],
  "united-states": ["france", "marbella", "dubai"],
  maldives: ["mauritius", "dubai", "greece"],
}

const DESTINATION_COPY = {
  mauritius: {
    en: "Beachfront estates and resort-led residences",
    fr: "Villas pieds dans l'eau et residences resort",
  },
  dubai: {
    en: "Penthouses, branded residences and waterfront towers",
    fr: "Penthouses, residences signees et adresses waterfront",
  },
  marbella: {
    en: "Golden Mile villas, sea views and private estates",
    fr: "Golden Mile, vues mer et villas privees",
  },
  france: {
    en: "Paris, Riviera and alpine addresses",
    fr: "Paris, Riviera et adresses alpines",
  },
  greece: {
    en: "Island villas and refined Mediterranean retreats",
    fr: "Villas insulaires et retraites mediterraneennes",
  },
  "united-states": {
    en: "Urban trophy homes and coastal estates",
    fr: "Residences iconiques et domaines cotiers",
  },
  maldives: {
    en: "Private island residences and resort villas",
    fr: "Iles privees et villas resort",
  },
}

const normalize = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()

const hasRenderableFluid = fluid =>
  Boolean(fluid && (fluid.src || fluid.srcSet || fluid.base64))

const getBackgroundStyle = fluid =>
  fluid?.src
    ? {
        backgroundImage: `url(${fluid.src})`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }
    : {}

const detectCurrentKey = (countryName = "") => {
  const v = normalize(countryName)
  if (v.includes("maurice") || v.includes("mauritius")) return "mauritius"
  if (v.includes("dubai") || v.includes("emirats") || v.includes("uae")) return "dubai"
  if (v.includes("marbella") || v.includes("espagne") || v.includes("spain")) return "marbella"
  if (v.includes("france")) return "france"
  if (v.includes("grece") || v.includes("greece")) return "greece"
  if (v.includes("etats-unis") || v.includes("united states") || v.includes("usa")) {
    return "united-states"
  }
  if (v.includes("maldives")) return "maldives"
  return ""
}

const pickDestinations = (locale, currentCountryName, limit = 6) => {
  const list = Object.entries(DESTINATION_META).map(([key, meta]) => ({
    key,
    label: meta[locale]?.label || meta.en.label,
    path: meta[locale]?.path || meta.en.path,
  }))
  const currentKey = detectCurrentKey(currentCountryName)
  const byKey = new Map(list.map((item) => [item.key, item])).set(
    currentKey,
    null
  )
  const priorities = PRIORITY_BY_CURRENT[currentKey] || []

  const selected = priorities.map((key) => byKey.get(key)).filter(Boolean)
  const fallback = list.filter(
    (item) =>
      item.key !== currentKey &&
      !selected.some((picked) => picked.key === item.key)
  )
  return selected.concat(fallback).slice(0, limit)
}

export default function OtherDestinationsChips({
  locale = "en",
  currentCountryName = "",
  eyebrow,
  title,
  description = "",
  className = "container pb-12 md:pb-16 lg:pb-20",
}) {
  const lang = locale === "fr" ? "fr" : "en"
  const data = useStaticQuery(graphql`
    query OtherDestinationsCountriesQuery {
      en: allDatoCmsCountry(filter: { locale: { eq: "en" } }) {
        nodes {
          name
          image {
            fluid(
              maxWidth: 1024
              imgixParams: { fm: "jpg", auto: "compress", w: "1024", q: 55 }
            ) {
              ...GatsbyDatoCmsFluid
            }
          }
        }
      }
      fr: allDatoCmsCountry(filter: { locale: { eq: "fr" } }) {
        nodes {
          name
          image {
            fluid(
              maxWidth: 1024
              imgixParams: { fm: "jpg", auto: "compress", w: "1024", q: 55 }
            ) {
              ...GatsbyDatoCmsFluid
            }
          }
        }
      }
    }
  `)

  const chips = pickDestinations(lang, currentCountryName, 6)
  const countries = lang === "fr" ? data.fr.nodes : data.en.nodes

  const cards = useMemo(() => {
    return chips
      .map((chip) => {
        const candidates = (DESTINATION_META[chip.key]?.countryCandidates || []).map(
          normalize
        )
        const country = countries.find((item) =>
          candidates.includes(normalize(item.name))
        )
        return {
          ...chip,
          description:
            DESTINATION_COPY[chip.key]?.[lang] ||
            DESTINATION_COPY[chip.key]?.en ||
            "",
          fluid: country?.image?.fluid || null,
        }
      })
      .filter((item) => hasRenderableFluid(item.fluid))
  }, [chips, countries, lang])

  if (!cards.length) return null

  const eyebrowText =
    eyebrow ||
    (lang === "fr" ? "Autres destinations a considerer" : "Other destinations to consider")
  const titleText =
    title || (lang === "fr" ? "Destinations de prestige" : "Prestige destinations")
  const primaryCards = cards.slice(0, 3)
  const secondaryCards = cards.slice(3)

  return (
    <section className={`${className} bg-white`}>
      <div className="mx-auto max-w-[1120px] border-t border-black/10 pt-10 md:pt-14">
        <div className="mb-7 grid gap-5 md:mb-9 md:grid-cols-[minmax(0,1fr)_minmax(300px,0.48fr)] md:items-end">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#86868b]">
              {eyebrowText}
            </p>
            <h2 className="mt-3 max-w-3xl text-[32px] font-semibold leading-[1.04] text-[#1d1d1f] md:text-[48px]">
              {titleText}
            </h2>
          </div>
          <p className="max-w-md text-[15px] leading-[1.65] text-[#6e6e73] md:justify-self-end md:text-[16px]">
            {description ||
              (lang === "fr"
                ? "Explorez une selection de lieux rares pour prolonger votre recherche vers d'autres marches de prestige."
                : "Explore a curated set of rare destinations to extend your search across other prestige markets.")}
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {primaryCards.map((item, index) => {
            const isFeatured = index === 0
            return (
              <Link
                to={item.path}
                className={`group relative min-h-[330px] overflow-hidden rounded-[8px] bg-[#111] shadow-[0_18px_54px_rgba(15,23,42,0.1)] transition duration-500 hover:-translate-y-0.5 hover:shadow-[0_28px_70px_rgba(15,23,42,0.16)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 ${
                  isFeatured ? "lg:col-span-2" : ""
                }`}
                key={item.key}
              >
                <div
                  className="absolute inset-0 h-full w-full transition duration-700 group-hover:scale-[1.035]"
                  style={getBackgroundStyle(item.fluid)}
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.08)_0%,rgba(0,0,0,0.18)_38%,rgba(0,0,0,0.74)_100%)] transition duration-500 group-hover:bg-[linear-gradient(180deg,rgba(0,0,0,0.04)_0%,rgba(0,0,0,0.18)_36%,rgba(0,0,0,0.82)_100%)]" />
                <div className="relative flex h-full min-h-[330px] flex-col justify-between p-6 text-white md:p-7">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-white/72">
                      {isFeatured
                        ? lang === "fr"
                          ? "Marche phare"
                          : "Featured market"
                        : lang === "fr"
                        ? "Destination"
                        : "Destination"}
                    </p>
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/14 text-[18px] leading-none text-white ring-1 ring-white/70 backdrop-blur transition group-hover:bg-white group-hover:text-[#1d1d1f] group-hover:ring-white">
                      &rarr;
                    </span>
                  </div>
                  <div className="max-w-[30rem]">
                    <p
                      className={`font-semibold leading-[0.98] text-white ${
                        isFeatured ? "text-[42px] md:text-[56px]" : "text-[32px]"
                      }`}
                    >
                      {item.label}
                    </p>
                    {item.description && (
                      <p className="mt-3 max-w-[24rem] text-[14px] leading-[1.45] text-white/78 md:text-[15px]">
                        {item.description}
                      </p>
                    )}
                    <span className="mt-5 inline-flex text-[14px] font-medium text-white/86">
                      {lang === "fr" ? "Voir les biens" : "View properties"}
                    </span>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>

        {secondaryCards.length > 0 && (
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {secondaryCards.map((item) => (
              <Link
                key={item.key}
                to={item.path}
                className="group relative min-h-[230px] overflow-hidden rounded-[8px] bg-[#111] shadow-[0_14px_44px_rgba(15,23,42,0.075)] transition duration-500 hover:-translate-y-0.5 hover:shadow-[0_22px_58px_rgba(15,23,42,0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
              >
                <div
                  className="absolute inset-0 h-full w-full transition duration-700 group-hover:scale-[1.035]"
                  style={getBackgroundStyle(item.fluid)}
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.04)_0%,rgba(0,0,0,0.2)_42%,rgba(0,0,0,0.76)_100%)] transition duration-500 group-hover:bg-[linear-gradient(180deg,rgba(0,0,0,0.02)_0%,rgba(0,0,0,0.2)_38%,rgba(0,0,0,0.84)_100%)]" />
                <div className="relative flex h-full min-h-[230px] flex-col justify-between p-5 text-white md:p-6">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-white/70">
                      {lang === "fr" ? "Autre marche" : "Other market"}
                    </p>
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/14 text-[16px] leading-none text-white ring-1 ring-white/70 backdrop-blur transition group-hover:bg-white group-hover:text-[#1d1d1f] group-hover:ring-white">
                      &rarr;
                    </span>
                  </div>
                  <div>
                    <p className="text-[28px] font-semibold leading-[1] text-white md:text-[32px]">
                      {item.label}
                    </p>
                    {item.description && (
                      <p className="mt-2 max-w-[22rem] text-[13px] leading-[1.45] text-white/76 md:text-[14px]">
                        {item.description}
                      </p>
                    )}
                    <span className="mt-4 inline-flex text-[13px] font-medium text-white/86 md:text-[14px]">
                      {lang === "fr" ? "Voir les biens" : "View properties"}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
